import type { CSSProperties } from 'react';
import { Button as RACButton } from 'react-aria-components';

import type { PillSpec } from '@/spaces/chat/chatEmptyStateData';
import { cn } from '@/utils/cn';

type PillChipProps = {
	pill: PillSpec;
	onClick?: () => void;
	isDisabled?: boolean;
	viewTransitionName?: string;
	isPulsating?: boolean;
	count?: number;
};

const CHIP_BASE_CLASSES = cn(
	'flex items-center gap-2.5 rounded-[10px] bg-white px-4 py-2.5',
	'text-sm font-medium text-(--text-primary)',
	'disabled:cursor-not-allowed disabled:opacity-50',
	'outline-none',
);

const CHIP_STATIC_SHADOW = cn(
	'transition-shadow duration-150',
	`shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_3px_0_rgba(0,0,0,0.04),0_2px_8px_0_rgba(0,0,0,0.06)]
	hover:shadow-[0_0_0_1px_rgba(0,0,0,0.12),0_2px_6px_0_rgba(0,0,0,0.08)]`,
);

export const PillChip = ({
	pill,
	onClick,
	isDisabled,
	viewTransitionName,
	isPulsating,
	count,
}: PillChipProps) => {
	const chipClasses = cn(
		CHIP_BASE_CLASSES,
		isPulsating ? 'animate-pill-hint-pulse' : CHIP_STATIC_SHADOW,
	);
	const style: CSSProperties | undefined = viewTransitionName
		? ({ viewTransitionName } as CSSProperties)
		: undefined;

	const inner = (
		<>
			<span
				className="flex h-[18px] w-[18px] shrink-0 items-center justify-center
					text-(--text-secondary)"
			>
				<pill.Icon />
			</span>
			{pill.label}
			{count != null && (
				<span
					className="flex h-[18px] min-w-[18px] shrink-0 items-center
						justify-center rounded-full bg-black px-1.5 text-[11px]
						font-semibold text-white"
				>
					{count}
				</span>
			)}
		</>
	);

	if (onClick) {
		return (
			<RACButton
				isDisabled={isDisabled}
				onPress={onClick}
				className={cn(chipClasses, 'cursor-pointer')}
				style={style}
			>
				{inner}
			</RACButton>
		);
	}

	return (
		<span className={chipClasses} style={style}>
			{inner}
		</span>
	);
};
